import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';
import { Modal } from '../Partials/Modal';
import { Button } from '../Partials/Button';
import classNames from 'classnames';
import { Collapse } from 'react-collapse';
import { InsuranceFormSubmissionStatus } from '../Utils/types';
import { FormSubmittedStatus } from './FormSubmittedStatus';
import { LoadingAnimation } from '../../LoadingAnimation/LoadingAnimation';
import { StreamLineIcon, StreamLineIcons } from '../../SvgIcon/StreamLineIcon';

interface SingleFieldModalProps {
  id: string;
  header: string;
  subHeader: string;
  submitButtonLabel: string;
  closeButtonLabel: string;
  inputLabel: string;
  inputPlaceholder: string;
  submitButtonIcon: StreamLineIcons;
  formSubmissionStatus: InsuranceFormSubmissionStatus;
  successMessage: string;
  errorMessage: string;
  defaultValue?: string;
  footerContent?: React.ReactNode;
  onSubmit: (inputValue: string) => void;
  onClose: () => void;
}

export const SingleFieldModal: FC<SingleFieldModalProps> = (props) => {
  const {
    header,
    subHeader,
    submitButtonLabel,
    closeButtonLabel,
    inputLabel,
    inputPlaceholder,
    submitButtonIcon,
    id,
    formSubmissionStatus,
    successMessage,
    errorMessage,
    defaultValue,
    footerContent,
    onClose,
    onSubmit,
  } = props;

  const [inputValue, setInputValue] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [canValidate, setCanValidate] = useState<boolean>(false);

  useEffect(() => {
    if (defaultValue) {
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  const onValueChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (canValidate) {
      validateValue(value);
    }
  };

  const validateValue = (value: string): boolean => {
    setIsError(value.length === 0);
    return value.length > 0;
  };

  const handleOnSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formSubmissionStatus === 'loading') return;

    if (!validateValue(inputValue)) {
      setCanValidate(true);
      return;
    }

    onSubmit(inputValue);
  };

  const headerId = `singleFieldModal_header_${id}`;
  const textareaId = `singleFieldModal_field_${id}`;
  const errorId = `singleFieldModal_error_${id}`;

  return (
    <Modal
      isOpen
      onClose={onClose}
      ariaLabeledBy={headerId}
      withoutCloseBtn
    >
      <div className="singleFieldModal__content">
        <div className="singleFieldModal__headerWrapper">
          <h1 id={headerId}>{header}</h1>
          <p className="singleFieldModal__subHeader">{subHeader}</p>
        </div>
        <form onSubmit={handleOnSubmit} className="singleFieldModal__form" noValidate>
          <div className="singleFieldModal__textareaWrapper">
            <label htmlFor={textareaId} className="singleFieldModal__textareaLabel">{inputLabel}</label>
            <textarea
              name="text"
              id={textareaId}
              className={classNames("singleFieldModal__textarea", { ["singleFieldModal__textarea--error"]: isError })}
              onChange={onValueChange}
              value={inputValue}
              placeholder={inputPlaceholder}
              required
              aria-describedby={errorId}
              disabled={formSubmissionStatus !== 'initial'}
            />
          </div>
          <Collapse isOpened={isError} theme={{ collapse: "react-collapse--collapse" }}>
            <p id={errorId} className="singleFieldModal__errorText">
              This field is required
            </p>
          </Collapse>
          {(formSubmissionStatus === 'initial' || formSubmissionStatus === 'loading') && (
            <div className="insuranceFormControls insuranceFormControls--equalWidth">
              <Button
                className="insuranceFormControls__button"
                type="solid"
                size="xlarge"
                tier="quaternary"
                onClick={onClose}
              >
                <span className="insuranceButton__label">
                  <StreamLineIcon
                    iconName="s-close-with-padding"
                    className="insuranceFormControls__icon"
                  />
                  <span className="insuranceFormControls__label">
                    {closeButtonLabel}
                  </span>
                </span>
              </Button>
              <Button
                className="insuranceFormControls__button"
                type="solid"
                size="xlarge"
                tier="tertiary"
                htmlType="submit"
              >
                <span className="insuranceButton__label">
                  <StreamLineIcon
                    iconName={submitButtonIcon}
                    className="insuranceFormControls__icon"
                  />
                  <span className="insuranceFormControls__label">
                    {submitButtonLabel}
                    <span className="singleFieldModal__loading">
                      {formSubmissionStatus === "loading" && (
                        <LoadingAnimation className="singleFieldModal__loadingItem" />
                      )}
                    </span>
                  </span>
                </span>
              </Button>
            </div>
          )}
          {(formSubmissionStatus === 'success' || formSubmissionStatus === 'error') && (
            <FormSubmittedStatus
              status={formSubmissionStatus}
              successMessage={successMessage}
              errorMessage={errorMessage}
            />
          )}
        </form>
        {footerContent && (
          <div className="singleFieldModal__footer">
            {footerContent}
          </div>
        )}
      </div>
    </Modal>
  )
};
